import { mat3, vec2, vec3 } from 'gl-matrix';
import { WindowLevel } from '../patient/windowLevel';
import { DcmJsDatasetWrapper } from './dcmJsWrapper';
import {
    CtAcquisitionAttributes,
    DicomImageSlice,
    DicomImageSliceAttributes,
    MrAcquisitionAttributes,
    PetAcquisitionAttributes,
} from './dicomImageSlice';
import { DicomDataset } from './engine/dataset';
import { DicomFile } from './engine/reader';
import { Tag, formatTag } from './engine/tag';
import * as tags from './engine/tags';

/**
 * Builds a {@link DicomImageSlice} from the typed dataset model.
 *
 * The equivalent that reads dcmjs's keyword-addressed shape lives in
 * `dicomProcessor`; this one exists so the slice-streaming path can go
 * straight from parsed bytes to a slice without projecting the dataset into
 * that shape first — the projection copies the pixel buffer out, which is
 * what makes a large image cost far more than its parse.
 *
 * Reading through the model also removes the shape-guessing the other version
 * has to do: a multi-valued attribute is an array whether it holds one value
 * or six, so `Array.isArray(x) ? x[0] : x` has nothing left to decide.
 */

/** How far a direction cosine may sit from 0 or ±1 and still count as axial. */
const ORIENTATION_TOLERANCE = 1e-4;

/** The largest difference between row and column spacing treated as uniform. */
const PIXEL_SPACING_TOLERANCE_MM = 1e-2;

/** A required attribute the dataset does not carry. */
export class MissingAttributeError extends Error {
    readonly tag: Tag;

    constructor(tag: Tag, keyword: string) {
        super(
            `Missing mandatory DICOM attribute ${keyword} (${formatTag(tag)})`
        );
        this.name = 'MissingAttributeError';
        this.tag = tag;
    }
}

function requireString(
    dataset: DicomDataset,
    tag: Tag,
    keyword: string
): string {
    const value = dataset.string(tag);
    if (value === undefined || value === '') {
        throw new MissingAttributeError(tag, keyword);
    }
    return value;
}

/**
 * Reads an attribute whose value multiplicity the standard fixes, and refuses
 * anything else.
 *
 * The count is exact rather than a minimum: a position with four coordinates
 * or an orientation with seven is malformed, and taking the first few would be
 * choosing which of them to believe. Every value must also be finite, which is
 * what catches a component the file left empty.
 */
function requireNumbers(
    dataset: DicomDataset,
    tag: Tag,
    keyword: string,
    count: number
): readonly number[] {
    const values = dataset.numbers(tag);
    if (
        values.length !== count ||
        values.some((value) => !Number.isFinite(value))
    ) {
        throw new MissingAttributeError(tag, keyword);
    }
    return values;
}

/** The rotation taking image space to patient space, from the two row cosines. */
function orientationMatrix(cosines: readonly number[]): mat3 {
    const row = vec3.fromValues(cosines[0], cosines[1], cosines[2]);
    const column = vec3.fromValues(cosines[3], cosines[4], cosines[5]);
    const normal = vec3.create();
    vec3.cross(normal, row, column);
    return mat3.fromValues(
        row[0],
        row[1],
        row[2],
        column[0],
        column[1],
        column[2],
        normal[0],
        normal[1],
        normal[2]
    );
}

/** True when the image plane is not aligned with the patient axes. */
function isTilted(cosines: readonly number[]): boolean {
    return !cosines.every(
        (value) =>
            Math.abs(value) < ORIENTATION_TOLERANCE ||
            Math.abs(Math.abs(value) - 1.0) < ORIENTATION_TOLERANCE
    );
}

function ctAttributes(dataset: DicomDataset): CtAcquisitionAttributes {
    const kvp = dataset.number(tags.KVP);
    const ctdi = dataset.number(tags.CTDI_VOL);
    // ConvolutionKernel is multi-valued; the model always presents it as an
    // array, so there is no single-value shape to special-case.
    const kernels = dataset.strings(tags.CONVOLUTION_KERNEL);
    return {
        kvp: kvp === undefined ? undefined : `${kvp} kV`,
        ctdi: ctdi === undefined ? undefined : `${ctdi.toFixed(2)} mGy`,
        kernel: kernels.length > 0 ? kernels.join(', ') : undefined,
        protocolName: dataset.string(tags.PROTOCOL_NAME),
    };
}

function mrAttributes(dataset: DicomDataset): MrAcquisitionAttributes {
    return {
        scanningSequence: dataset.string(tags.SCANNING_SEQUENCE),
        mraAcquisitionType: dataset.string(tags.MR_ACQUISITION_TYPE),
        sequenceVariant: dataset.string(tags.SEQUENCE_VARIANT),
    };
}

function petAttributes(
    dataset: DicomDataset
): PetAcquisitionAttributes | undefined {
    // Always an array, so the item is either there or the sequence is empty.
    const [item] = dataset.sequence(
        tags.RADIOPHARMACEUTICAL_INFORMATION_SEQUENCE
    );
    if (!item) {
        return undefined;
    }
    return {
        radionuclideTotalDose: item.number(tags.RADIONUCLIDE_TOTAL_DOSE),
        radionuclideHalfLife: item.number(tags.RADIONUCLIDE_HALF_LIFE),
        radiopharmaceuticalStartTime: item.string(
            tags.RADIOPHARMACEUTICAL_START_TIME
        ),
        radiopharmaceuticalStartDateTime: item.string(
            tags.RADIOPHARMACEUTICAL_START_DATETIME
        ),
    };
}

function windowLevel(dataset: DicomDataset): WindowLevel | undefined {
    const width = dataset.number(tags.WINDOW_WIDTH);
    const center = dataset.number(tags.WINDOW_CENTER);
    if (width === undefined || center === undefined || width <= 0) {
        return undefined;
    }
    return new WindowLevel(width, center);
}

/**
 * Reads one image instance into a slice.
 *
 * @throws {MissingAttributeError} when an attribute the slice cannot be placed
 * without is absent. Placement depends on position, orientation and spacing,
 * so a slice missing any of them would be written into the volume at a depth
 * derived from nothing.
 */
export function readImageSlice(file: DicomFile): DicomImageSlice {
    const dataset = file.dataset;

    const sopInstanceUid = requireString(
        dataset,
        tags.SOP_INSTANCE_UID,
        'SOPInstanceUID'
    );
    const seriesInstanceUid = requireString(
        dataset,
        tags.SERIES_INSTANCE_UID,
        'SeriesInstanceUID'
    );
    const frameOfReferenceUid = requireString(
        dataset,
        tags.FRAME_OF_REFERENCE_UID,
        'FrameOfReferenceUID'
    );

    const cosines = requireNumbers(
        dataset,
        tags.IMAGE_ORIENTATION_PATIENT,
        'ImageOrientationPatient',
        6
    );
    const position = requireNumbers(
        dataset,
        tags.IMAGE_POSITION_PATIENT,
        'ImagePositionPatient',
        3
    );
    const spacing = requireNumbers(
        dataset,
        tags.PIXEL_SPACING,
        'PixelSpacing',
        2
    );

    if (Math.abs(spacing[0] - spacing[1]) >= PIXEL_SPACING_TOLERANCE_MM) {
        throw new Error(
            `Non-uniform pixel spacing: ${spacing[0].toFixed(3)}, ` +
                `${spacing[1].toFixed(3)}`
        );
    }

    const acquisition: DicomImageSliceAttributes = {
        patientWeightKg: dataset.number(tags.PATIENT_WEIGHT),
        seriesTime: dataset.string(tags.SERIES_TIME),
        acquisitionTime: dataset.string(tags.ACQUISITION_TIME),
        studyDate: dataset.string(tags.STUDY_DATE),
        seriesDate: dataset.string(tags.SERIES_DATE),
        acquisitionDate: dataset.string(tags.ACQUISITION_DATE),
        ct: ctAttributes(dataset),
        mr: mrAttributes(dataset),
        pet: petAttributes(dataset),
    };

    const slice = new DicomImageSlice(
        sopInstanceUid,
        dataset.string(tags.MODALITY) ?? '',
        vec2.fromValues(spacing[0], spacing[1]),
        vec3.fromValues(position[0], position[1], position[2]),
        orientationMatrix(cosines),
        dataset.number(tags.SLICE_THICKNESS) ?? NaN,
        dataset.string(tags.SERIES_DESCRIPTION) ?? '',
        seriesInstanceUid,
        frameOfReferenceUid,
        dataset.string(tags.MANUFACTURER) ?? '',
        dataset.string(tags.MANUFACTURER_MODEL_NAME) ?? '',
        dataset.number(tags.INSTANCE_NUMBER) as number,
        readImageType(dataset),
        dataset.number(tags.ROWS) as number,
        dataset.number(tags.COLUMNS) as number,
        dataset.string(tags.PATIENT_POSITION) ?? '',
        dataset.string(tags.STATION_NAME) ?? '',
        dataset.string(tags.FILTER_TYPE) ?? '',
        dataset.number(tags.RESCALE_INTERCEPT) ?? 0.0,
        dataset.number(tags.RESCALE_SLOPE) ?? 1.0,
        dataset.string(tags.PATIENT_POSITION) === 'HFP',
        isTilted(cosines),
        new DcmJsDatasetWrapper(file),
        acquisition
    );

    slice.windowLevel = windowLevel(dataset);
    return slice;
}

/** ImageType, defaulted the way the rest of the application expects it. */
function readImageType(dataset: DicomDataset): string[] {
    const values = dataset.strings(tags.IMAGE_TYPE);
    return values.length > 0 ? [...values] : ['UNKNOWN', 'UNKNOWN'];
}
